import React, { createContext, useState, useContext, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Cookies from 'js-cookie';

declare module '@mui/material/styles' {
  /** Surfaces and separators, resolved once for both modes.
   *
   *  These exist because components kept inventing them. A hand-rolled light/dark contrast
   *  ternary appeared 55 times across the app, and the alphas layered on it were picked per
   *  author — 0.1/0.06 in one file, 0.06/0.08 in another, which is inverted: one made dark
   *  lighter and the other made it darker.
   *
   *  The three dark panel colours in use (#141416, #1d1d20, #23232a) turned out NOT to be
   *  duplicates. They are an elevation scale — a base surface, a selected panel, and hover on
   *  that panel — applied consistently, just never named. So they are named here rather than
   *  collapsed, and the light mode keeps all three at #fff, because light mode separates
   *  surfaces with borders instead of elevation.
   *
   *  The reason any of this was local is a real gap rather than carelessness: Dialog and Paper
   *  both sit on background.paper, so a panel inside a dialog renders on its own colour and
   *  disappears in dark mode. Every author hit that and solved it where they stood.
   */
  interface Palette {
    surface: {
      /** The text colour's own hue. What a hand-written light/dark contrast ternary was for. */
      contrast: string;
      /** The surface a section sits on. Distinct from background.paper so panels inside a
       *  dialog do not vanish into it. */
      base: string;
      /** A panel lifted off that surface — selected, active, or called out. */
      raised: string;
      /** Hover on a raised panel. */
      raisedHover: string;
      /** The standard 1px separator around a panel. */
      border: string;
      /** Hover wash over an ordinary surface. */
      hover: string;
      /** The faintest tint that still reads as a separate area. */
      subtle: string;
    };
    status: {
      accent: string;
      complete: string;
      pending: string;
      retrying: string;
      error: string;
      notStarted: string;
    };
  }

  interface PaletteOptions {
    surface?: {
      contrast?: string;
      base?: string;
      raised?: string;
      raisedHover?: string;
      border?: string;
      hover?: string;
      subtle?: string;
    };
    status?: {
      accent?: string;
      complete?: string;
      pending?: string;
      retrying?: string;
      error?: string;
      notStarted?: string;
    };
  }
}

declare module '@mui/material/styles' {
  /** The corner radii this app uses, as CSS strings.
   *
   *  Strings on purpose, and this is the whole point of the group. Inside `sx`, MUI multiplies
   *  a bare number by shape.borderRadius (4) — so `borderRadius: 2` is 8px, and `borderRadius:
   *  5` is 20px. Outside `sx` — a style prop, styled(), styleOverrides — the same number is
   *  literal pixels. The app had 165 radius declarations spelled 40 different ways because of
   *  that, and on the assets page `5` (20px) sat next to `'5px'` (5px): someone wrote "5" both
   *  times and got a four-fold difference.
   *
   *  A string means the same thing everywhere, so the trap cannot be sprung.
   */
  interface Theme {
    radius: { sm: string; md: string; lg: string; pill: string };
  }
  interface ThemeOptions {
    radius?: { sm?: string; md?: string; lg?: string; pill?: string };
  }
}

interface IThemeContext {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

const UMS_ACCENT_RED = '#c62828';

/** The app's theme, for one mode.
 *
 *  Exported so tests render against the REAL theme instead of a hand-copied stub. A test that
 *  lists the palette keys its component happens to touch goes stale the moment the theme grows
 *  one — which is exactly how adding `surface` broke four passing tests. There is nothing to
 *  keep in step if there is only one definition.
 */
export const createUmsTheme = (darkMode: boolean) => {
  // Whatever colour text is in this mode. Named because it is used as a base to tint FROM,
  // not just to write in.
  const CONTRAST = darkMode ? '#fff' : '#000';

  return createTheme({
    // Named so nobody has to know the multiplier rule. sm/md/lg are the three radii already
    // dominant in the app (4px on 24 elements, 8px on 61, 10px on 23); pill is the fully
    // rounded end.
    radius: { sm: '4px', md: '8px', lg: '10px', pill: '999px' },
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#000' : '#fff',
      },
      action: {
        selected: darkMode ? '#3c3c3c' : '#fff',
      },
      background: {
        default: darkMode ? '#333' : '#ddd',
        paper: darkMode ? '#000' : '#fff',
      },
      text: {
        primary: darkMode ? '#fff' : '#111',
      },
      surface: {
        contrast: CONTRAST,
        // The existing scale, kept exactly. Light stays #fff at every level: light mode
        // separates surfaces with borders, and stepping it would change every panel in the app
        // for no gain.
        base: darkMode ? '#141416' : '#fff',
        raised: darkMode ? '#1d1d20' : '#fff',
        raisedHover: darkMode ? '#23232a' : '#fff',
        border: alpha(CONTRAST, darkMode ? 0.18 : 0.12),
        hover: alpha(CONTRAST, darkMode ? 0.10 : 0.06),
        subtle: alpha(CONTRAST, darkMode ? 0.06 : 0.04),
      },
      status: {
        accent: UMS_ACCENT_RED,
        complete: '#4caf50',   // Vibrant Green (like default MUI success)   // Vibrant Green
        pending: '#fdd835',
        retrying: '#f57c00',   // Bright Orange (a bit deeper than pending)
        error: '#f44336',      // Vibrant Red (like default MUI error)
        notStarted: '#9e9e9e', // Lighter, neutral Grey
      },
    },
    components: {
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--ums-accent-red': UMS_ACCENT_RED,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: darkMode ? alpha('#fff', 0.72) : alpha('#000', 0.65),
            '&.Mui-focused': {
              color: UMS_ACCENT_RED,
            },
            '&.Mui-disabled': {
              color: darkMode ? alpha('#fff', 0.42) : alpha('#000', 0.38),
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: darkMode ? '#fff' : '#111',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? alpha('#fff', 0.24) : alpha('#000', 0.23),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? alpha('#fff', 0.45) : alpha('#000', 0.45),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: UMS_ACCENT_RED,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? alpha('#fff', 0.18) : alpha('#000', 0.18),
            },
          },
          input: {
            '&::placeholder': {
              opacity: 1,
              color: darkMode ? alpha('#fff', 0.48) : alpha('#000', 0.45),
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: darkMode ? alpha('#fff', 0.9) : alpha('#000', 0.87),
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: darkMode ? alpha('#fff', 0.7) : alpha('#000', 0.54),
            '&.Mui-checked': {
              color: UMS_ACCENT_RED,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: darkMode ? alpha('#fff', 0.35) : alpha('#000', 0.28),
          },
          switchBase: {
            '&.Mui-checked': {
              color: UMS_ACCENT_RED,
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: UMS_ACCENT_RED,
              opacity: 0.9,
            },
          },
        },
      },
    },
  });
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return Cookies.get('darkMode') === 'true';
  });

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    Cookies.set('darkMode', newValue.toString(), { expires: 365 });
  };

  // Memoized so a re-render does not rebuild the whole theme object.
  const theme = useMemo(() => createUmsTheme(darkMode), [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};
